import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextAlign from '@tiptap/extension-text-align';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';

import { ResizableImage } from './image/ResizableImage';
import { FileUploadExtension } from './image/file-adapter';
import { EditorToolbar } from './EditorToolbar';
import { ResizableTable } from './table/ResizableTable';

import './editor.css';
import { MathInline } from './math/MathInline';
import { MathBlock } from './math/MathBlock';
import { useEffect } from 'react';
import { EnterAfterMathBlock } from './math/EnterAfterMathBlock';
import { preprocessRichHtml } from './markdownToHtml';

interface OnChangeHandler {
  (data: string): void;
}

export interface RichInputProp {
  initData?: string;
  placeholder?: string;
  onChange: OnChangeHandler;
  imageUrls?: string[];
}

export const RichInput = ({
  initData,
  placeholder,
  onChange,
  imageUrls,
}: RichInputProp) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Start typing your answer here...',
        emptyEditorClass: 'is-editor-empty',
      }),
      Underline,
      Subscript,
      Superscript,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      ResizableTable,
      TableRow,
      TableHeader,
      TableCell,
      TaskList,
      TaskItem.configure({ nested: true }),
      MathInline,
      MathBlock,
      FileUploadExtension,
      ResizableImage,
      EnterAfterMathBlock,
    ],
    content: preprocessRichHtml(initData || ''),
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const insertMath = () => {
    const formula = prompt('Enter LaTeX formula:', '\\int_a^b f(x)dx');
    if (formula) {
      editor?.commands.insertMathInline(formula);
    }
  };

  const insertTable = () => {
    editor?.commands.insertTable({ rows: 3, cols: 3, withHeaderRow: true });
  };

  useEffect(() => {
    if (editor && initData !== undefined) {
      const html = preprocessRichHtml(initData);
      const currentHtml = editor.getHTML();

      if (html !== currentHtml) {
        // defer to next tick
        setTimeout(() => {
          editor.commands.setContent(html, false);
          editor.view.dispatch(
            editor.view.state.tr.setMeta('history$reset', true),
          );
          editor.commands.setTextSelection(0);
          onChange(html);
        }, 0);
      }
    }
  }, [initData]);

  return (
    <div className="rich-editor p-2 rounded-sm border space-y-2">
      <EditorToolbar
        editor={editor}
        onInsertMath={insertMath}
        onInsertTable={insertTable}
        imageUrls={imageUrls}
      />
      <EditorContent editor={editor} />
    </div>
  );
};
